import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageSquare, Send, Mail, MailOpen, Circle, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerMessagesPage() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) fetchMessages();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/messages`);
      setMessages(data);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (msgId) => {
    try {
      await axios.put(`${API}/messages/${msgId}/read`);
      setMessages(msgs => msgs.map(m => m.id === msgId ? { ...m, read: true } : m));
    } catch {}
  };

  const deleteMessage = async (msgId) => {
    try {
      await axios.delete(`${API}/messages/${msgId}`);
      setMessages(msgs => msgs.filter(m => m.id !== msgId));
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyTo || !replyContent.trim()) return;
    setSending(true);
    try {
      const toId = replyTo.from_id === user?.id ? replyTo.to_id : replyTo.from_id;
      await axios.post(`${API}/messages`, { to_id: toId, content: replyContent });
      toast.success('Reply sent!');
      setReplyTo(null);
      setReplyContent('');
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const received = messages.filter(m => m.to_id === user?.id);

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="player-messages-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <MessageSquare className="w-8 h-8 text-cyan-400" />
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase text-white">Messages</h1>
            <p className="text-zinc-500 text-sm">{received.filter(m => !m.read).length} unread &bull; {received.length}/15</p>
          </div>
        </div>

        {/* Received */}
        <div>
          {received.length === 0 ? (
            <div className="text-center py-12 bg-[#121212] border border-zinc-800">
              <MailOpen className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No messages yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {received.map((msg) => (
                <div
                  key={msg.id}
                  className={`bg-[#121212] border px-4 py-2.5 ${!msg.read ? 'border-cyan-500/30' : 'border-zinc-800'}`}
                  onClick={() => !msg.read && markAsRead(msg.id)}
                  data-testid={`msg-received-${msg.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] text-cyan-400 font-bold">{msg.from_name.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="text-white font-medium text-sm">{msg.from_name}</span>
                    {!msg.read && <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-[10px]">New</span>}
                    <span className="text-zinc-400 text-sm flex-1 truncate ml-2">{msg.content}</span>
                    <span className="text-zinc-600 text-xs flex-shrink-0">{new Date(msg.created_at).toLocaleDateString()}</span>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {replyTo?.id !== msg.id && (
                        <button onClick={(e) => { e.stopPropagation(); setReplyTo(msg); setReplyContent(''); }} className="text-cyan-400 text-xs hover:text-cyan-300" data-testid={`reply-btn-${msg.id}`}>
                          Reply
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); deleteMessage(msg.id); }} className="text-zinc-600 hover:text-red-500 transition-colors" data-testid={`delete-msg-${msg.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {replyTo?.id === msg.id && (
                    <form onSubmit={handleReply} className="flex gap-2 mt-2 pl-8">
                      <Textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Type your reply..."
                        required
                        className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[40px] focus:border-cyan-400 resize-none text-sm flex-1"
                        data-testid="reply-input"
                      />
                      <div className="flex flex-col gap-1">
                        <Button type="submit" disabled={sending} size="sm" className="bg-cyan-400 text-black hover:bg-cyan-300 rounded-none h-7" data-testid="send-reply-button">
                          <Send className="w-3 h-3" />
                        </Button>
                        <button type="button" onClick={() => setReplyTo(null)} className="text-zinc-500 text-[10px] hover:text-white">Cancel</button>
                      </div>
                    </form>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
